export enum ErrorCode {
  ERROR = "ERROR",
  BOTH_INSURANCE_FAILURE = "BOTH_INSURANCE_FAILURE",
  OUT_OF_NETWORK = "OUT_OF_NETWORK",
  INSURANCE_FAILURE = "INSURANCE_FAILURE", // other
  INACTIVE_PLAN = "INACTIVE_PLAN",
  // new states per Availity Error States Discussion
  DOB_BAD_FORMAT = "DOB_BAD_FORMAT",
  DOB_MISMATCH = "DOB_MISMATCH",
  MEMBERID_ERROR = "MEMBERID_ERROR",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  INVALID_MEMBERID = "INVALID_MEMBERID",
  MEMBERID_MISMATCH = "MEMBERID_MISMATCH",
  INVALID_NAME = "INVALID_NAME",
  NAME_MISMATCH = "NAME_MISMATCH",
  INVALID_INFO = "INVALID_INFO",
  INVALID_SUBSCRIBER = "INVALID_SUBSCRIBER",
  INVALID_PROVIDER = "INVALID_PROVIDER",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  PAYER_INCOMPLETE = "PAYER_INCOMPLETE",
  PAYER_NOT_SUPPORTED = "PAYER_NOT_SUPPORTED",
  IRRELEVANT_INSURANCE = "IRRELEVANT_INSURANCE",
  NO_PLANS_FOUND = "NO_PLANS_FOUND",
  SERVICE_CODE_NOT_SUPPORTED = "SERVICE_CODE_NOT_SUPPORTED",
  MEDICARE_MEMBERID_ERROR = "MEDICARE_MEMBERID_ERROR",
  BENEFIT_NOT_FOUND = "BENEFIT_NOT_FOUND",
  SUBSCRIBER_NOT_FOUND = "SUBSCRIBER_NOT_FOUND",
  IRREVELANT_INSURANCE = "IRREVELANT_INSURANCE",
}

export type ErrorCodeType = `${ErrorCode}`;

export const getErrorText = (
  errorCode: ErrorCodeType,
  // biome-ignore lint/suspicious/noExplicitAny: translation function type varies
  t: (key: any) => string,
  retryCount = 0,
) => {
  switch (errorCode) {
    case "OUT_OF_NETWORK":
      return t("OutOfNetwork");
    case "DOB_BAD_FORMAT":
      return retryCount >= 1
        ? t("PlanCannotBeLocated")
        : t("ReviewDateOfBirth");
    case "DOB_MISMATCH":
      return retryCount >= 1
        ? t("PlanCannotBeLocated")
        : t("ReviewDateOfBirth");
    case "MEDICARE_MEMBERID_ERROR":
      return retryCount >= 3
        ? t("PlanCannotBeLocated")
        : t("ReviewMedicareNumber");
    case "INVALID_INFO": // pass
    case "INVALID_NAME": // pass
    case "NAME_MISMATCH": // pass
    case "INVALID_SUBSCRIBER":
    case "SUBSCRIBER_NOT_FOUND":
      return retryCount >= 1
        ? t("PlanCannotBeLocated")
        : t("SomethingNotRightReviewDetails");
    case "INSURANCE_FAILURE":
    case "BENEFIT_NOT_FOUND":
      return t("PlanCannotBeLocated");
    case "ERROR":
      return retryCount >= 1
        ? t("PlanCannotBeLocated")
        : t("SomethingNotRightReviewDetails");
    case "NO_PLANS_FOUND":
      return retryCount >= 1
        ? t("PlanCannotBeLocated")
        : t("NotAViableInsurer");
    case "MEMBERID_ERROR":
      return retryCount >= 1 ? t("PlanCannotBeLocated") : t("ReviewMemberId");
    case "INVALID_MEMBERID":
      return retryCount >= 1 ? t("PlanCannotBeLocated") : t("ReviewMemberId");
    case "MEMBERID_MISMATCH":
      return retryCount >= 1 ? t("PlanCannotBeLocated") : t("ReviewMemberId");
    case "INACTIVE_PLAN":
      return t("PlanInactive");
    case "PAYER_INCOMPLETE":
      return t("ProviderOffline");
    case "IRREVELANT_INSURANCE":
      return t("PlanNotSupported");
    default:
      return t("PlanCannotBeLocated");
  }
};

export const getErrorBlurb = (
  errorCode: ErrorCodeType,
  // biome-ignore lint/suspicious/noExplicitAny: translation function type varies
  t: (key: any) => string,
  retryCount = 0,
) => {
  switch (errorCode) {
    case "OUT_OF_NETWORK":
      return t("ProceedWithoutInsuranceOrContactAdmin");
    case "DOB_BAD_FORMAT":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureDateOfBirthCorrect");
    case "DOB_MISMATCH":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureDateOfBirthCorrect");
    case "MEDICARE_MEMBERID_ERROR":
      return retryCount >= 3
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectNumber");
    case "INVALID_INFO":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectProviderAndDetails");
    case "INVALID_NAME":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectProviderAndDetails");
    case "NAME_MISMATCH":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectProviderAndDetails");
    case "INVALID_SUBSCRIBER":
    case "SUBSCRIBER_NOT_FOUND":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("PlanMayNoLongerBeActive");
    case "ERROR":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("PlanMayNoLongerBeActive");
    case "NO_PLANS_FOUND":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectDetails");
    case "MEMBERID_ERROR":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectIdNumber");
    case "INVALID_MEMBERID":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectIdNumber");
    case "MEMBERID_MISMATCH":
      return retryCount >= 1
        ? t("ProceedWithoutInsuranceOrContactAdmin")
        : t("EnsureCorrectIdNumber");
    case "INACTIVE_PLAN":
      return "";
    case "PAYER_INCOMPLETE":
      return t("ProviderDoesNotSupportWebVerification");
    case "INSURANCE_FAILURE":
    case "BENEFIT_NOT_FOUND":
      return t("EnsureCorrectDetails");
    default: // What should the default be?
      return t("ProceedWithoutInsuranceOrContactAdmin");
  }
};

export const getErrorTextOnPC = (errorCode: ErrorCodeType, retryCount = 0) => {
  switch (errorCode) {
    case "OUT_OF_NETWORK":
      return "The patient's plan is out of network";
    case "DOB_BAD_FORMAT":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Review the patient's Date of Birth.";
    case "DOB_MISMATCH":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Review the patient's Date of Birth.";
    case "MEDICARE_MEMBERID_ERROR":
      return retryCount >= 3
        ? "The patient's plan cannot be located."
        : "Review the patient's Medicare Number.";
    case "INVALID_INFO": // pass
    case "INVALID_NAME":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Something's not right. Review the patient's details.";
    case "NAME_MISMATCH":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Something's not right. Review the patient's details.";
    case "INVALID_SUBSCRIBER":
    case "SUBSCRIBER_NOT_FOUND":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Something's not right. Review the patient's details.";
    case "INSURANCE_FAILURE": // pass
    case "BENEFIT_NOT_FOUND": // pass
    case "ERROR":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Something's not right. Review the patient's details.";
    case "NO_PLANS_FOUND":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Not a viable insurer.";
    case "MEMBERID_ERROR":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Review the patient's Member ID.";
    case "INVALID_MEMBERID":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Review the patient's Member ID.";
    case "MEMBERID_MISMATCH":
      return retryCount >= 1
        ? "The patient's plan cannot be located."
        : "Review the patient's Member ID.";
    case "INACTIVE_PLAN":
      return "The patient's plan is inactive.";
    case "PAYER_INCOMPLETE":
      return "The patient's insurance provider is offline.";
    case "IRREVELANT_INSURANCE":
      return "The patient's plan is not supported.";
    default:
      return "The patient's plan cannot be located.";
  }
};

export const getErrorBlurbOnPC = (errorCode: ErrorCodeType) => {
  switch (errorCode) {
    case "OUT_OF_NETWORK":
      return "Please use a different insurance, or remove insurance and proceed.";
    case "DOB_BAD_FORMAT":
      return "DoB Format is incorrect. Please correct";
    case "DOB_MISMATCH":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "MEDICARE_MEMBERID_ERROR":
      return "Please ensure the Medicare number matches the red, white and blue card. Name and DoB must also match";
    case "INVALID_INFO":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "INVALID_NAME":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "NAME_MISMATCH":
      return "The member ID and name do not match";
    case "INVALID_SUBSCRIBER":
    case "SUBSCRIBER_NOT_FOUND":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "ERROR":
      return "Please contact Superscript at support@superscript.nyc for more information";
    case "NO_PLANS_FOUND":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "MEMBERID_ERROR":
      return "MemberID includes unrecognized characters (check for ' )";
    case "INVALID_MEMBERID":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
    case "MEMBERID_MISMATCH":
      return "The member ID and name do not match";
    case "INACTIVE_PLAN":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card. Contact Superscript for more information";
    case "PAYER_INCOMPLETE":
      return "The payer is offline";
    case "IRREVELANT_INSURANCE":
      return "This is a dental/vision/life insurance policy. Contact Superscript for more information";
    case "TIMEOUT_ERROR":
      return "The payer is responding slowly - please run check again";
    case "PAYER_NOT_SUPPORTED":
      return "The payer does not return data";
    case "SERVICE_CODE_NOT_SUPPORTED":
      return "Please contact Superscript at support@superscript.nyc for more information";
    case "INVALID_PROVIDER":
      return "Please contact Superscript at support@superscript.nyc for more information";
    case "INSURANCE_FAILURE":
    case "BENEFIT_NOT_FOUND":
      return "Please ensure Insured Name, DoB, and MemberID match insurance card. Contact Superscript for more information";
    default:
      return "Please ensure Insured Name, DoB, and MemberID match insurance card";
  }
};
